import React, { useState } from "react";
import { useLang, useSettings, cldUrl } from "../lib/providers";
import { BadgeCheck, ImageIcon, X } from "lucide-react";

export default function About() {
  const { lang } = useLang();
  const { settings } = useSettings();
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  if (!settings.about_enabled) return null;
  const title = lang === "ar" ? settings.about_title_ar : settings.about_title_fr;
  const text = lang === "ar" ? settings.about_text_ar : settings.about_text_fr;
  const sinceLabel = lang === "ar" ? settings.about_since_label_ar : settings.about_since_label_fr;
  const images = (settings.about_image_urls || []).filter(Boolean);
  const hidePhotosByDefault = settings.about_photos_hidden_by_default !== false;

  return (
    <section id="about" className="py-20 sm:py-28 theme-section relative" data-testid="about-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <h2 className="font-heading font-black uppercase tracking-tighter text-4xl sm:text-5xl leading-[0.9]">{title}</h2>
            {sinceLabel && (
              <div className="mt-6 flex items-center gap-2 text-xs text-neon font-bold uppercase tracking-widest">
                <BadgeCheck className="w-4 h-4" /> {sinceLabel}
              </div>
            )}
          </div>
          <div className="lg:col-span-7">
            <p className="text-white/80 text-base sm:text-lg leading-relaxed whitespace-pre-line">{text}</p>

            {/* Photos behind icon trigger (when hidden_by_default) */}
            {images.length > 0 && hidePhotosByDefault && (
              <button
                type="button"
                onClick={() => { setActiveIdx(0); setGalleryOpen(true); }}
                className="mt-8 inline-flex items-center gap-2 border border-neon/40 text-neon px-4 py-2.5 text-xs font-black uppercase tracking-widest hover:bg-neon/5 transition-colors"
                data-testid="about-show-photos-btn"
              >
                <ImageIcon className="w-4 h-4" />
                {lang === "ar" ? `شاهد صوري (${images.length})` : `Voir mes photos (${images.length})`}
              </button>
            )}

            {/* If user un-ticked hidden_by_default in CMS, show the grid as before */}
            {images.length > 0 && !hidePhotosByDefault && (
              <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {images.map((src, i) => (
                  <div key={i} className="aspect-square overflow-hidden bg-black/40 border border-white/10 cursor-pointer" onClick={() => { setActiveIdx(i); setGalleryOpen(true); }}>
                    <img src={cldUrl(src, { w: 480 })} srcSet={`${cldUrl(src, { w: 320 })} 320w, ${cldUrl(src, { w: 480 })} 480w, ${cldUrl(src, { w: 640 })} 640w`} sizes="(max-width: 640px) 50vw, 33vw" alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox gallery */}
      {galleryOpen && images.length > 0 && (
        <div className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setGalleryOpen(false)} data-testid="about-gallery">
          <button onClick={() => setGalleryOpen(false)} className="absolute top-4 end-4 text-white hover:text-neon z-10" aria-label="close"><X className="w-6 h-6" /></button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={cldUrl(images[activeIdx], { w: 1600 })} alt="" className="w-full max-h-[80vh] object-contain" decoding="async" />
            {images.length > 1 && (
              <div className="mt-4 grid grid-cols-6 sm:grid-cols-8 gap-2">
                {images.map((src, i) => (
                  <button key={i} onClick={() => setActiveIdx(i)} className={`aspect-square overflow-hidden border ${activeIdx === i ? "border-neon" : "border-white/10 hover:border-white/30"}`}>
                    <img src={cldUrl(src, { w: 160 })} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
